import { type Ref, useEffect } from 'react'
import { DeepPartial } from '@apollo/client/utilities'
import { Card, Form, FormInstance, Input, Select, Switch } from 'antd'

import type { BaseFormValues } from './formTypes'
import { layout } from '~/constants/forms'
import {
  ValidityInterval,
  type SchemeAddonInput,
} from '~/gql/generated/graphql'
import { AddonTypeSelect } from './components'
import { CurrencyInput } from '~/components/shared/numberInputs'
import { currencyFormItemConversionProps } from '../Product/utils'
import { VALIDITY_INTERVAL_LABELS } from '~/constants/domain'

type Props = {
  loading?: boolean
  initialValues?: DeepPartial<SchemeAddonInput>
  formRef: Ref<FormInstance<BaseFormValues>>
}
const AddonDetailsForm = ({ initialValues, loading, formRef }: Props) => {
  const [form] = Form.useForm()

  const isProtected = Form.useWatch('is_protected', form)
  const addonType = Form.useWatch('addon_type', form)

  // Clear `role_whitelist` if `is_protected` is un-set
  useEffect(() => {
    // Not if its undefined tho, or it affects the initial values
    if (isProtected === false) {
      form.resetFields(['role_whitelist'])
    }
  }, [isProtected, form])

  return (
    <Form<BaseFormValues>
      {...layout}
      layout="horizontal"
      form={form}
      name="addonForm"
      initialValues={initialValues}
      disabled={loading}
      ref={formRef}
    >
      <Card title="Addon details">
        <Form.Item name="scheme_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          name="addon_type"
          label="Addon type"
          rules={[{ required: true, message: 'Please choose a addon type' }]}
        >
          <AddonTypeSelect disabled={Boolean(initialValues?.addon_type)} />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please specify a name' }]}
          tooltip="This is visible in the app"
        >
          <Input disabled={!addonType} />
        </Form.Item>
      </Card>

      <Card title="Addon settings">
        <Form.Item
          name="validity"
          label="Valid for"
          rules={[
            {
              required: true,
              message: 'Please specify a validity period',
            },
          ]}
        >
          <Select<ValidityInterval>
            options={Object.entries(VALIDITY_INTERVAL_LABELS).map(
              ([value, label]) => ({
                value,
                label,
              }),
            )}
          />
        </Form.Item>

        <Form.Item
          name="amount"
          label="Price"
          {...currencyFormItemConversionProps}
          rules={[{ required: true, message: 'Please specify a price' }]}
        >
          <CurrencyInput disabled={!addonType} />
        </Form.Item>
      </Card>

      <Card title="Visibility">
        <Form.Item
          name="is_discoverable"
          label="Discoverable 🎟️"
          help="Viewable in the app. Hidden addons can be used for vouchers."
        >
          <Switch defaultChecked={initialValues?.is_discoverable ?? true} />
        </Form.Item>

        <Form.Item
          name="is_protected"
          label="Protected 🔒"
          help="Available only to users with the specified roles"
        >
          <Switch
            defaultChecked={initialValues?.is_protected ?? false}
            disabled={!addonType}
          />
        </Form.Item>

        <Form.Item
          name="role_whitelist"
          label="Roles allowed 🔒"
          rules={[
            {
              required: isProtected,
              message: 'Please select roles who are eligible for this addon.',
            },
          ]}
        >
          <Select
            options={[
              { value: 'member', label: 'Member' },
              { value: 'external_stakeholder', label: 'External Stakeholder' },
            ]}
            mode="multiple"
            disabled={!isProtected || !addonType}
          />
        </Form.Item>
      </Card>
      <Card title="Danger zone">
        <Form.Item
          name="is_active"
          label="Active"
          help="De-activating an addon means it can't be used"
          tooltip="You might want to make it non-discoverable first, so it can still be debited by people who have it."
        >
          <Switch
            defaultChecked={initialValues?.is_active ?? false}
            disabled={!addonType}
          />
        </Form.Item>
      </Card>
    </Form>
  )
}

export default AddonDetailsForm
